import moment from 'moment-timezone';

import type { I18nService } from '@recruitee/i18n';

const round = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;

export function getMinutes(ms: number) {
  return round(moment.duration(ms, 'ms').asMinutes());
}

export function getHours(ms: number) {
  return round(moment.duration(ms, 'ms').asHours());
}

export function getDays(ms: number) {
  return round(moment.duration(ms, 'ms').asDays());
}

const rules: Array<{
  range: [min: number, max: number];
  format: (seconds: number, i18n: I18nService) => string;
}> = [
  { range: [Number.NEGATIVE_INFINITY, 0], format: () => `-` },
  // seconds (long)
  {
    range: [1, 60],
    format: (seconds, i18n) => i18n.translate('app.common.x_seconds', { count: seconds }),
  },
  // minutes + seconds (abbr)
  {
    range: [61, 599],
    format: (seconds, i18n: I18nService) => {
      const duration = moment.duration(seconds, 'seconds');
      const durationMinutes = duration.minutes();
      const durationSeconds = duration.subtract(duration.minutes(), 'm').seconds();
      if (durationSeconds > 0) {
        return `${durationMinutes}m ${durationSeconds}s`;
      }

      return i18n.translate('app.common.x_minutes', {
        count: Math.round(getMinutes(seconds * 1000)),
      });
    },
  },
  // minutes rounded (long)
  {
    range: [600, 3599],
    format: (seconds, i18n: I18nService) =>
      i18n.translate('app.common.x_minutes', {
        count: Math.round(getMinutes(seconds * 1000)),
      }),
  },
  // hours (long)
  {
    range: [3600, 60 * 60 * 23.5],
    format: (seconds, i18n: I18nService) =>
      i18n.translate('app.common.x_hours_plural', {
        count: Math.round(getHours(seconds * 1000)),
      }),
  },
  // days (long)
  {
    range: [60 * 60 * 23.5, Number.POSITIVE_INFINITY],
    format: (seconds, i18n: I18nService) =>
      i18n.translate('app.common.x_days_plural', {
        count: Math.round(getDays(seconds * 1000)),
      }),
  },
];

export const formatDuration = (seconds: number, i18n: I18nService): string => {
  const x = rules.find(({ range: [min, max], format }) => min <= seconds && max >= seconds);

  if (x) return x.format(seconds, i18n);

  return `${seconds}s`;
};
